import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  FiTrendingUp, FiTrendingDown, FiMinus, FiSend, FiAlertTriangle,
  FiShield, FiArrowRight, FiZap, FiClock, FiImage, FiX, FiCornerDownRight,
  FiBookmark, FiEdit2, FiTrash2,
} from 'react-icons/fi';
import { FaStar, FaRocket } from 'react-icons/fa';
import { useAuth } from '../../Context/AuthContext';
import { useAuthGate } from '../../Context/AuthGateContext';
import { Avatar } from '../Achievements/Achievements';
import { PixelArt, PixelEmpty, MarketMood, PixelBurst, PixelMedal } from '../../lib/pixelart';
import './Community.css';

// Tiers — mismos cortes que el Marketplace para mantener coherencia visual
const TIERS = [
  { min: 500, frame: 'legend',  label: 'Leyenda',  color: '#d946ef' },
  { min: 200, frame: 'magenta', label: 'Veterano', color: '#c084fc' },
  { min: 100, frame: 'gold',    label: 'Experto',  color: '#ffd700' },
  { min: 50,  frame: 'cyan',    label: 'Avanzado', color: '#22d3ee' },
  { min: 10,  frame: 'neon',    label: 'Activo',   color: '#00d412' },
  { min: 0,   frame: 'basic',   label: 'Nuevo',    color: '#8a9bb0' },
];
const tierOf = (t) => TIERS.find((x) => (t || 0) >= x.min);

const ASSETS = ['BTC', 'ETH', 'BNB', 'SOL', 'LTC', 'TRX', 'USDT', 'USDC'];
const ASSET_COLOR = {
  BTC: '#f7931a', ETH: '#627eea', BNB: '#f0b90b', SOL: '#9945ff',
  LTC: '#bfbbbb', TRX: '#ef0027', USDT: '#26a17b', USDC: '#2775ca',
};

// Logo circular de moneda (reusa los SVG de public/assets/crypto-logos) — se auto-oculta si falta.
function CoinIcon({ sym, size = 14 }) {
  return (
    <img
      src={`/assets/crypto-logos/${String(sym).toLowerCase()}.svg`}
      alt=""
      width={size}
      height={size}
      style={{ borderRadius: '50%', verticalAlign: 'middle', flexShrink: 0 }}
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
    />
  );
}

const SENTIMENTS = {
  bull:    { label: 'Alcista', color: 'var(--fin-up)',   Icon: FiTrendingUp },
  bear:    { label: 'Bajista', color: 'var(--fin-down)', Icon: FiTrendingDown },
  neutral: { label: 'Neutral', color: '#8a9bb0',          Icon: FiMinus },
};

// ── Reacciones pixel (sprites en src/lib/pixelart) ──
const REACTIONS = [
  { key: 'rocket', label: 'A la luna' },
  { key: 'fire', label: 'Caliente' },
  { key: 'diamond', label: 'Diamond hands' },
];

// Flecha de voto pixel (color por currentColor → cambia con el estado)
function VoteArrow({ down }) {
  return (
    <svg width="15" height="12" viewBox="0 0 5 4" shapeRendering="crispEdges" aria-hidden="true" style={down ? { transform: 'rotate(180deg)' } : undefined}>
      <rect x="2" y="0" width="1" height="1" fill="currentColor" />
      <rect x="1" y="1" width="3" height="1" fill="currentColor" />
      <rect x="0" y="2" width="5" height="1" fill="currentColor" />
    </svg>
  );
}

const MAX_LEN     = 280;
const COOLDOWN_MS = 30_000;
const LS_POSTS    = 'bitplify_community_posts_v2';
const LS_VOTES    = 'bitplify_community_votes_v2';
const LS_REACTS   = 'bitplify_community_reacts_v2';
const LS_COMMENTS = 'bitplify_community_comments_v2';
const LS_HIDDEN   = 'bitplify_community_hidden_v2';
const LS_LAST     = 'bitplify_community_last_v2';
const LS_SAVED    = 'bitplify_community_saved_v2';

// Imagen semilla: mini-chart de velas neón generado por código (data URL SVG)
const CHART_IMG = (() => {
  const cols = [[36, 78, '#00d412'], [28, 64, '#00d412'], [44, 100, '#e53935'], [38, 86, '#00d412'], [54, 116, '#e53935'], [30, 76, '#00d412'], [20, 60, '#00d412']];
  let r = `<rect width='320' height='180' fill='#0a0e0b'/><rect x='0' y='90' width='320' height='1' fill='#1d2c1e'/>`;
  cols.forEach(([top, bot, c], i) => { const x = 22 + i * 42; r += `<rect x='${x + 8}' y='${top - 12}' width='2' height='${bot - top + 24}' fill='${c}'/><rect x='${x}' y='${top}' width='18' height='${bot - top}' fill='${c}'/>`; });
  return 'data:image/svg+xml,' + encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='320' height='180' shape-rendering='crispEdges'>${r}</svg>`);
})();

// Posts semilla — chatter de mercado de traders reales del marketplace
const SEED_POSTS = [
  { id: 'c-seed-1', author: { name: 'DatuPanda',   country: 'CR', trades: 210, rating: 4.8 }, sentiment: 'bull',    assets: ['BTC'],        text: 'BTC apoyándose en los 62k otra vez 🚀 Si cierra la semanal por encima vamos a buscar 68k. Sigo acumulando en cada dip vía P2P.', score: 34, reacts: { rocket: 21, fire: 9, diamond: 12 }, image: CHART_IMG, mins: 12 },
  { id: 'c-seed-2', author: { name: 'VIPER',       country: 'CO', trades: 180, rating: 4.8 }, sentiment: 'bear',    assets: ['ETH'],        text: 'Ojo con $ETH en el corto plazo, lo veo pesado abajo de 3.4k. Espero una corrección antes de volver a entrar largo. No es consejo, es lo que hago yo.', score: 21, reacts: { rocket: 4, fire: 11, diamond: 2 }, mins: 38 },
  { id: 'c-seed-3', author: { name: 'Syl',         country: 'AR', trades: 110, rating: 4.5 }, sentiment: 'neutral', assets: ['USDT'],       text: 'Con la inflación acá, dolarizar en $USDT no es especular, es sobrevivir. El P2P es la forma más sana de hacerlo a precio real.', score: 57, reacts: { rocket: 8, fire: 5, diamond: 22 }, mins: 72 },
  { id: 'c-seed-4', author: { name: 'BlockBoss',   country: 'US', trades: 320, rating: 5.0 }, sentiment: 'bull',    assets: ['SOL', 'BTC'], text: 'Rotando una parte de $BTC a $SOL. El ecosistema de Solana no para y las fees son ridículamente bajas. Hold de largo plazo para mí.', score: 42, reacts: { rocket: 18, fire: 7, diamond: 9 }, mins: 140 },
  { id: 'c-seed-5', author: { name: 'CryptoLuna',  country: 'VE', trades: 85,  rating: 4.3 }, sentiment: 'bull',    assets: ['USDT', 'BTC'],text: 'En Venezuela el P2P es la única puerta real al dólar cripto. Acá la gente no especula, se protege. Cuídense de los que ofrecen "mejor tasa por fuera".', score: 88, reacts: { rocket: 30, fire: 12, diamond: 41 }, mins: 220 },
  { id: 'c-seed-6', author: { name: 'EddyInvestor',country: 'MX', trades: 200, rating: 4.8 }, sentiment: 'bear',    assets: ['BNB'],        text: 'Tomé ganancias en $BNB, lo veo lateral un buen rato. Rotando a stables y esperando que el mercado defina dirección. Paciencia > FOMO.', score: 18, reacts: { rocket: 3, fire: 4, diamond: 6 }, mins: 330 },
];

// Comentarios semilla (con un hilo anidado) para mostrar la UI tipo Reddit
const SEED_COMMENTS = {
  'c-seed-1': [
    { id: 'cm-1', parentId: null,  author: { name: 'VIPER',     trades: 180 }, text: 'Coincido, mientras aguante los 60k el sesgo es alcista. Buen punto de acumulación.', score: 8,  mins: 9 },
    { id: 'cm-2', parentId: 'cm-1', author: { name: 'DatuPanda', trades: 210 }, text: 'Exacto. Si pierde 60k recién ahí me preocupo. Por ahora compro tranquilo.', score: 5,  mins: 6 },
    { id: 'cm-3', parentId: null,  author: { name: 'Eli',       trades: 160 }, text: '¿Vos cómo cubrís el riesgo si gira en contra? 🤔', score: 2, mins: 3 },
  ],
  'c-seed-5': [
    { id: 'cm-4', parentId: null, author: { name: 'Syl', trades: 110 }, text: 'Tal cual. El que te ofrece operar afuera el 99% de las veces es scam. Dentro de la app al menos tenés protección: vendedores verificados, reputación y disputas. 🔒', score: 14, mins: 40 },
  ],
};

// ── Guardarraíl: detecta intentos de compartir contacto / operar fuera de la plataforma ──
function detectContact(text) {
  const t = (text || '').toLowerCase();
  const reasons = [];
  if (/[\w.+-]+@[\w-]+\.[a-z]{2,}/i.test(text)) reasons.push('email');
  if (/(https?:\/\/|www\.|t\.me\/|wa\.me\/|chat\.whatsapp|\b[a-z0-9-]+\.(?:com|net|io|me|co|app|xyz|org|link|gg)\b)/i.test(text)) reasons.push('un enlace');
  if (/\b(telegram|telegran|whats?app|whatsap|wsp|wpp|w\.?app|signal|discord|instagram|\binsta\b|\big\b|t\.me|wa\.me)\b/i.test(t)) reasons.push('una red externa');
  if (/(\+\d[\d\s().-]{6,}\d)/.test(text) || /\b(mi|tu|el)\s*(n[úu]mero|tel[eé]fono|celular|cel|whats|wsp|correo)\b/i.test(t)) reasons.push('datos de contacto');
  if (/\b(por\s*fuera|fuera\s*de\s*la\s*(app|plataforma|p[aá]gina)|por\s*afuera|hablamos\s*afuera|escrib[ií]me|mand[aá]me|pasame\s*tu|te\s*paso\s*mi|contact[aá]me\s*por)\b/i.test(t)) reasons.push('operar fuera de Bitplify');
  return { found: reasons.length > 0, reasons: [...new Set(reasons)] };
}

const readLS = (k, fallback) => { try { const v = JSON.parse(localStorage.getItem(k)); return v ?? fallback; } catch { return fallback; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); return true; } catch { return false; } };

function timeLabel(ts) {
  const m = Math.floor((Date.now() - ts) / 60000);
  if (m < 1) return 'ahora';
  if (m < 60) return `hace ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h}h`;
  return `hace ${Math.floor(h / 24)}d`;
}

// Resalta cashtags ($BTC) dentro del texto
function renderText(text, onAsset) {
  return String(text).split(/(\$[A-Za-z]{2,6})/g).map((part, i) => {
    const m = part.match(/^\$([A-Za-z]{2,6})$/);
    const sym = m && m[1].toUpperCase();
    if (sym && ASSETS.includes(sym)) {
      return <button key={i} type="button" className="cf-cashtag" style={{ color: ASSET_COLOR[sym] }} onClick={() => onAsset(sym)}><CoinIcon sym={sym} size={13} /> ${sym}</button>;
    }
    return <span key={i}>{part}</span>;
  });
}

// Comprime/redimensiona la imagen antes de guardarla en localStorage
function compressImage(file, maxDim = 1000, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) { const r = Math.min(maxDim / width, maxDim / height); width = Math.round(width * r); height = Math.round(height * r); }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject; img.src = reader.result;
    };
    reader.onerror = reject; reader.readAsDataURL(file);
  });
}

export default function CommunityFeed({ offers = [], onViewAsset, onNavigate }) {
  const { user } = useAuth();
  const { openAuth } = useAuthGate();

  const [userPosts, setUserPosts]   = useState(() => readLS(LS_POSTS, []));
  const [votes, setVotes]           = useState(() => readLS(LS_VOTES, {}));
  const [reacts, setReacts]         = useState(() => readLS(LS_REACTS, {}));
  const [userComments, setUserComments] = useState(() => readLS(LS_COMMENTS, {}));
  const [hidden, setHidden]         = useState(() => readLS(LS_HIDDEN, []));
  const [saved, setSaved]           = useState(() => readLS(LS_SAVED, []));
  const [editingId, setEditingId]   = useState(null);   // post propio en edición
  const [editDraft, setEditDraft]   = useState('');

  // compositor
  const [text, setText]           = useState('');
  const [sentiment, setSentiment] = useState('neutral');
  const [picked, setPicked]       = useState([]);
  const [image, setImage]         = useState(null);
  const [warn, setWarn]           = useState(null);
  const fileRef = useRef(null);

  // feed / hilos
  const [toast, setToast]   = useState(null);
  const [sort, setSort]     = useState('recent');
  const [assetFilter, setAssetFilter] = useState('all');
  const [sentFilter, setSentFilter]   = useState('all');   // all | bull | bear
  const [savedOnly, setSavedOnly]     = useState(false);
  const [openThreads, setOpenThreads] = useState(() => new Set());
  const [activeReply, setActiveReply] = useState(null);   // commentId
  const [replyDraft, setReplyDraft]   = useState('');
  const [topDraft, setTopDraft]       = useState({});     // { postId: text }
  const [burst, setBurst]             = useState(0);      // celebración al publicar

  const seedPosts = useMemo(() => SEED_POSTS.map((p) => ({ ...p, ts: Date.now() - p.mins * 60000, seed: true })), []);
  const offerCount = useCallback((sym) => offers.filter((o) => (o.currency || '').toUpperCase() === sym).length, [offers]);

  const myVote = (id) => votes[id] || 0;
  const scoreOf = (id, base) => (base || 0) + (votes[id] || 0);
  const reactCount = (postId, key, base) => (base || 0) + (reacts[postId]?.[key] ? 1 : 0);

  const commentsOf = useCallback((postId) => {
    const seeds = (SEED_COMMENTS[postId] || []).map((c) => ({ ...c, ts: Date.now() - c.mins * 60000 }));
    return [...seeds, ...(userComments[postId] || [])];
  }, [userComments]);

  const posts = useMemo(() => {
    const hiddenSet = new Set(hidden);
    const savedSet  = new Set(saved);
    let list = [...userPosts, ...seedPosts].filter((p) => !hiddenSet.has(p.id));
    if (assetFilter !== 'all') list = list.filter((p) => (p.assets || []).includes(assetFilter));
    if (sentFilter !== 'all')  list = list.filter((p) => p.sentiment === sentFilter);
    if (savedOnly)             list = list.filter((p) => savedSet.has(p.id));
    list.sort((a, b) => (sort === 'popular' ? (scoreOf(b.id, b.score) - scoreOf(a.id, a.score)) || (b.ts - a.ts) : b.ts - a.ts));
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPosts, seedPosts, hidden, saved, assetFilter, sentFilter, savedOnly, sort, votes]);

  // 🔥 Tendencias: qué activos se hablan más + sentimiento por moneda (estilo StockTwits)
  const trending = useMemo(() => {
    const map = {};
    [...userPosts, ...seedPosts].filter((p) => !new Set(hidden).has(p.id)).forEach((p) => {
      (p.assets || []).forEach((a) => {
        if (!ASSETS.includes(a)) return;
        map[a] = map[a] || { sym: a, count: 0, bull: 0, bear: 0 };
        map[a].count++;
        if (p.sentiment === 'bull') map[a].bull++;
        else if (p.sentiment === 'bear') map[a].bear++;
      });
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [userPosts, seedPosts, hidden]);

  // Ánimo del Muro a partir del sentimiento de las publicaciones visibles
  const mood = useMemo(() => {
    let bull = 0, bear = 0;
    posts.forEach((p) => { if (p.sentiment === 'bull') bull++; else if (p.sentiment === 'bear') bear++; });
    return { bull, bear, kind: bear > bull ? 'bear' : 'bull', total: bull + bear };
  }, [posts]);

  const showToast = (msg) => { setToast(msg); window.clearTimeout(showToast._t); showToast._t = window.setTimeout(() => setToast(null), 2600); };
  const needAuth = () => { if (!user) { openAuth('register'); return true; } return false; };

  // ── acciones ──
  const vote = (id, dir) => {
    if (needAuth()) return;
    setVotes((prev) => { const cur = prev[id] || 0; const next = { ...prev }; if (cur === dir) delete next[id]; else next[id] = dir; writeLS(LS_VOTES, next); return next; });
  };
  const toggleReact = (postId, key) => {
    if (needAuth()) return;
    setReacts((prev) => { const cur = { ...(prev[postId] || {}) }; cur[key] = !cur[key]; if (!cur[key]) delete cur[key]; const next = { ...prev, [postId]: cur }; if (!Object.keys(cur).length) delete next[postId]; writeLS(LS_REACTS, next); return next; });
  };
  const toggleThread = (postId) => setOpenThreads((prev) => { const n = new Set(prev); n.has(postId) ? n.delete(postId) : n.add(postId); return n; });

  const togglePick = (sym) => setPicked((arr) => arr.includes(sym) ? arr.filter((x) => x !== sym) : [...arr, sym]);

  const onPickImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('Ese archivo no es una imagen.'); return; }
    try { setImage(await compressImage(file)); } catch { showToast('No se pudo procesar la imagen.'); }
    e.target.value = '';
  };

  const authorFromUser = () => ({ name: user.username || user.name || 'vos', country: user.country, trades: user.trades || 0, rating: user.rating, avatar: user.avatar });

  const publish = () => {
    if (needAuth()) return;
    const body = text.trim();
    if (!body && !image) return;
    const hit = detectContact(body);
    if (hit.found) { setWarn(hit.reasons); return; }
    const last = readLS(LS_LAST, 0);
    const wait = Math.ceil((COOLDOWN_MS - (Date.now() - last)) / 1000);
    if (wait > 0) { showToast(`Esperá ${wait}s antes de publicar de nuevo.`); return; }

    const post = { id: `c-${Date.now()}`, author: authorFromUser(), sentiment, assets: picked, text: body, image, score: 0, ts: Date.now() };
    setUserPosts((prev) => { const next = [post, ...prev]; if (!writeLS(LS_POSTS, next)) { showToast('La imagen es muy pesada para guardar localmente.'); return prev; } return next; });
    writeLS(LS_LAST, Date.now());
    setText(''); setPicked([]); setSentiment('neutral'); setImage(null); setWarn(null);
    setBurst(Date.now());
    showToast('Publicado en el Muro 🚀');
  };

  const addComment = (postId, parentId, raw, clear) => {
    if (needAuth()) return;
    const body = (raw || '').trim();
    if (!body) return;
    const hit = detectContact(body);
    if (hit.found) { showToast(`No publicamos ${hit.reasons[0]} — operá siempre dentro de Bitplify.`); return; }
    const cm = { id: `cm-${Date.now()}`, parentId: parentId || null, author: authorFromUser(), text: body, score: 0, ts: Date.now() };
    setUserComments((prev) => { const next = { ...prev, [postId]: [...(prev[postId] || []), cm] }; writeLS(LS_COMMENTS, next); return next; });
    setOpenThreads((prev) => new Set(prev).add(postId));
    clear();
  };

  const hidePost = (p) => {
    setHidden((prev) => { const next = [...new Set([...prev, p.id])]; writeLS(LS_HIDDEN, next); return next; });
    showToast('Gracias — lo revisaremos. Publicación ocultada.');
  };

  // ¿es un post mío (editable/borrable)? — no semilla y mismo autor
  const myName = user ? (user.username || user.name) : null;
  const isMine = (p) => !p.seed && myName && p.author?.name === myName;

  const toggleSave = (id) => {
    if (needAuth()) return;
    setSaved((prev) => { const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]; writeLS(LS_SAVED, next); return next; });
  };

  const deletePost = (id) => {
    setUserPosts((prev) => { const next = prev.filter((p) => p.id !== id); writeLS(LS_POSTS, next); return next; });
    showToast('Publicación eliminada.');
  };

  const startEdit = (p) => { setEditingId(p.id); setEditDraft(p.text || ''); };
  const saveEdit = (id) => {
    const body = editDraft.trim();
    const hit = detectContact(body);
    if (hit.found) { showToast(`No publicamos ${hit.reasons[0]} — operá siempre dentro de Bitplify.`); return; }
    setUserPosts((prev) => { const next = prev.map((p) => p.id === id ? { ...p, text: body, edited: true } : p); writeLS(LS_POSTS, next); return next; });
    setEditingId(null); setEditDraft('');
    showToast('Publicación actualizada.');
  };

  // ── render de un comentario (recursivo, hilos tipo Reddit) ──
  const renderComment = (c, childrenMap, depth) => {
    const kids = childrenMap[c.id] || [];
    const tier = tierOf(c.author.trades);
    const v = myVote(c.id);
    return (
      <div key={c.id} className={`cf-cmt ${depth > 0 ? 'nested' : ''}`}>
        <div className="cf-cmt-main">
          <div className="cf-cmt-head">
            <button type="button" className="cf-cmt-author" onClick={() => onNavigate && onNavigate(c.author.name)}>@{c.author.name}</button>
            <span className="cf-cmt-tier" style={{ color: tier.color }}>{tier.label}</span>
            <span className="cf-cmt-time">· {timeLabel(c.ts)}</span>
          </div>
          <div className="cf-cmt-text">{renderText(c.text, (s) => onViewAsset && onViewAsset(s))}</div>
          <div className="cf-cmt-actions">
            <span className="cf-vote sm">
              <button type="button" className={`cf-vote-btn up ${v === 1 ? 'on' : ''}`} onClick={() => vote(c.id, 1)}><VoteArrow /></button>
              <span className={`cf-vote-score ${v === 1 ? 'up' : v === -1 ? 'down' : ''}`}>{scoreOf(c.id, c.score)}</span>
              <button type="button" className={`cf-vote-btn down ${v === -1 ? 'on' : ''}`} onClick={() => vote(c.id, -1)}><VoteArrow down /></button>
            </span>
            <button type="button" className="cf-cmt-reply" onClick={() => { if (needAuth()) return; setActiveReply(activeReply === c.id ? null : c.id); setReplyDraft(''); }}>
              <FiCornerDownRight /> Responder
            </button>
          </div>
          {activeReply === c.id && (
            <div className="cf-reply-box">
              <textarea className="cf-cmt-input" rows={2} placeholder={`Respondiendo a @${c.author.name}…`} value={replyDraft} onChange={(e) => setReplyDraft(e.target.value.slice(0, MAX_LEN))} autoFocus />
              <div className="cf-reply-actions">
                <button type="button" className="cf-link-btn" onClick={() => setActiveReply(null)}>Cancelar</button>
                <button type="button" className="cf-cmt-send" disabled={!replyDraft.trim()} onClick={() => addComment(activeReply._postId ?? c._postId, c.id, replyDraft, () => { setReplyDraft(''); setActiveReply(null); })}><FiSend /> Responder</button>
              </div>
            </div>
          )}
          {kids.length > 0 && <div className="cf-cmt-kids">{kids.map((k) => renderComment(k, childrenMap, depth + 1))}</div>}
        </div>
      </div>
    );
  };

  const renderThread = (post) => {
    const all = commentsOf(post.id).map((c) => ({ ...c, _postId: post.id }));
    const byParent = {};
    all.forEach((c) => { (byParent[c.parentId || 'root'] = byParent[c.parentId || 'root'] || []).push(c); });
    Object.values(byParent).forEach((arr) => arr.sort((a, b) => a.ts - b.ts));
    const roots = byParent.root || [];
    const draft = topDraft[post.id] || '';
    return (
      <div className="cf-thread">
        <div className="cf-cmt-compose">
          <textarea className="cf-cmt-input" rows={2} placeholder={user ? 'Sumá tu comentario…' : 'Iniciá sesión para comentar'} value={draft} onFocus={() => { if (!user) openAuth('register'); }} onChange={(e) => setTopDraft((d) => ({ ...d, [post.id]: e.target.value.slice(0, MAX_LEN) }))} />
          <button type="button" className="cf-cmt-send" disabled={!draft.trim()} onClick={() => addComment(post.id, null, draft, () => setTopDraft((d) => ({ ...d, [post.id]: '' })))}><FiSend /> Comentar</button>
        </div>
        {roots.length === 0
          ? <div className="cf-cmt-empty">Sé el primero en comentar este análisis.</div>
          : roots.map((c) => renderComment(c, byParent, 0))}
      </div>
    );
  };

  return (
    <div className="cf-wrap">
      {/* Banner de normas */}
      <div className="cf-banner">
        <FiShield className="cf-banner-ic" />
        <div className="cf-banner-txt">
          <strong>Muro de la comunidad.</strong> Hablá de mercado, compartí tu análisis y tu sentimiento.
          Nunca compartas datos de contacto ni operes por fuera: <strong>solo dentro de Bitplify tenés protección — vendedores verificados, reputación y disputas.</strong>
        </div>
      </div>

      {/* Compositor */}
      <div className="cf-compose">
        <PixelBurst trigger={burst} />
        {user ? (
          <>
            <div className="cf-compose-top">
              <Avatar u={{ username: user.username || user.name, name: user.name, avatar: user.avatar }} size={40} />
              <textarea className="cf-input" placeholder="¿Qué estás viendo en el mercado? Compartí tu análisis…" value={text} onChange={(e) => { setText(e.target.value.slice(0, MAX_LEN)); if (warn) setWarn(null); }} rows={2} />
            </div>

            {image && (
              <div className="cf-img-preview">
                <img src={image} alt="adjunto" />
                <button type="button" className="cf-img-remove" onClick={() => setImage(null)} aria-label="Quitar imagen"><FiX /></button>
              </div>
            )}

            {warn && (
              <div className="cf-warn">
                <FiAlertTriangle className="cf-warn-ic" />
                <span>Tu mensaje incluye <strong>{warn.join(', ')}</strong>. Por tu seguridad no lo publicamos: operá siempre dentro de Bitplify, donde tenés protección y disputas si algo sale mal.</span>
              </div>
            )}

            <div className="cf-compose-tools">
              <div className="cf-sentiments">
                {Object.entries(SENTIMENTS).map(([key, s]) => { const Icon = s.Icon; return (
                  <button key={key} type="button" className={`cf-sent ${sentiment === key ? 'active' : ''}`} style={sentiment === key ? { color: s.color, borderColor: s.color } : undefined} onClick={() => setSentiment(key)}><Icon /> {s.label}</button>
                ); })}
              </div>
              <div className="cf-compose-right">
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickImage} />
                <button type="button" className="cf-img-btn" onClick={() => fileRef.current?.click()} title="Adjuntar imagen"><FiImage /></button>
                <span className={`cf-count ${text.length > MAX_LEN - 30 ? 'warn' : ''}`}>{MAX_LEN - text.length}</span>
                <button type="button" className="cf-publish" disabled={!text.trim() && !image} onClick={publish}><FiSend /> Publicar</button>
              </div>
            </div>

            <div className="cf-asset-pick">
              <span className="cf-asset-pick-lbl">Activos:</span>
              {ASSETS.map((a) => (
                <button key={a} type="button" className={`cf-asset-chip ${picked.includes(a) ? 'active' : ''}`} style={{ ...(picked.includes(a) ? { color: ASSET_COLOR[a], borderColor: ASSET_COLOR[a] } : {}), display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => togglePick(a)}><CoinIcon sym={a} size={14} /> ${a}</button>
              ))}
            </div>
          </>
        ) : (
          <div className="cf-guest">
            <div className="cf-guest-txt"><FiZap className="cf-guest-ic" /> Sumate a la conversación del mercado. Leé gratis; para publicar, votar y comentar, creá tu cuenta.</div>
            <button className="cf-guest-btn" onClick={() => openAuth('register')}>Crear cuenta</button>
          </div>
        )}
      </div>

      {/* 🔥 Tendencias — qué se habla más + sentimiento por moneda */}
      {trending.length > 0 && (
        <div className="cf-trending">
          <div className="cf-trending-head"><FiZap className="cf-trending-ic" /> Tendencias <span>lo más comentado ahora</span></div>
          <div className="cf-trending-list">
            {trending.map((t) => {
              const tot = t.bull + t.bear;
              const bullPct = tot > 0 ? Math.round((t.bull / tot) * 100) : 50;
              const lean = tot === 0 ? 'neutral' : t.bull > t.bear ? 'bull' : t.bull < t.bear ? 'bear' : 'neutral';
              return (
                <button key={t.sym} type="button" className={`cf-trend ${assetFilter === t.sym ? 'active' : ''}`} onClick={() => setAssetFilter(assetFilter === t.sym ? 'all' : t.sym)}>
                  <div className="cf-trend-top">
                    <span className="cf-trend-sym" style={{ color: ASSET_COLOR[t.sym], display: 'inline-flex', alignItems: 'center', gap: 5 }}><CoinIcon sym={t.sym} size={16} /> ${t.sym}</span>
                    <span className="cf-trend-count"><span className="fin-num">{t.count}</span> {t.count === 1 ? 'post' : 'posts'}</span>
                  </div>
                  <div className="cf-trend-bar"><i className="bull" style={{ width: `${bullPct}%` }} /><i className="bear" style={{ width: `${100 - bullPct}%` }} /></div>
                  <span className={`cf-trend-lean ${lean}`}>{tot === 0 ? 'Sin sesgo' : `${bullPct}% alcista`}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="cf-filters">
        <div className="cf-sort">
          <button type="button" className={sort === 'recent' ? 'active' : ''} onClick={() => setSort('recent')}><FiClock /> Recientes</button>
          <button type="button" className={sort === 'popular' ? 'active' : ''} onClick={() => setSort('popular')}><FaRocket /> Populares</button>
          <span className="cf-sort-div" />
          <button type="button" className={sentFilter === 'bull' ? 'active' : ''} onClick={() => setSentFilter(sentFilter === 'bull' ? 'all' : 'bull')}><FiTrendingUp /> Alcistas</button>
          <button type="button" className={sentFilter === 'bear' ? 'active' : ''} onClick={() => setSentFilter(sentFilter === 'bear' ? 'all' : 'bear')}><FiTrendingDown /> Bajistas</button>
          {user && <button type="button" className={savedOnly ? 'active' : ''} onClick={() => setSavedOnly((s) => !s)}><FiBookmark /> Guardados</button>}
        </div>
        <div className="cf-asset-filter">
          <button type="button" className={`cf-fchip ${assetFilter === 'all' ? 'active' : ''}`} onClick={() => setAssetFilter('all')}>Todos</button>
          {ASSETS.map((a) => (
            <button key={a} type="button" className={`cf-fchip ${assetFilter === a ? 'active' : ''}`} style={{ ...(assetFilter === a ? { color: ASSET_COLOR[a], borderColor: ASSET_COLOR[a] } : {}), display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => setAssetFilter(a)}><CoinIcon sym={a} size={14} /> ${a}</button>
          ))}
        </div>
      </div>

      {/* Ánimo del Muro (toro/oso pixel según sentimiento de las publicaciones) */}
      {mood.total > 0 && (
        <div className={`cf-mood ${mood.kind}`}>
          <MarketMood mood={mood.kind} px={3} className="cf-mood-art" />
          <div className="cf-mood-txt">
            <span className="cf-mood-lbl">Ánimo del Muro</span>
            <strong className="cf-mood-val">{mood.kind === 'bull' ? 'Alcista' : 'Bajista'}</strong>
          </div>
          <div className="cf-mood-meta"><span className="fin-num">{mood.bull}</span> alcistas · <span className="fin-num">{mood.bear}</span> bajistas</div>
        </div>
      )}

      {/* Feed */}
      <div className="cf-feed">
        {posts.length === 0 && (
          <PixelEmpty sprite="chest" title="Nada por acá todavía" hint={assetFilter === 'all' ? 'Sé el primero en compartir tu análisis del mercado.' : `No hay publicaciones sobre $${assetFilter}. ¡Abrí la conversación!`} />
        )}
        {posts.map((p) => {
          const tier = tierOf(p.author.trades);
          const s = SENTIMENTS[p.sentiment] || SENTIMENTS.neutral;
          const SIcon = s.Icon;
          const v = myVote(p.id);
          const nComments = commentsOf(p.id).length;
          const open = openThreads.has(p.id);
          return (
            <article key={p.id} className="cf-post">
              {/* columna de voto Reddit */}
              <div className="cf-vote">
                <button type="button" className={`cf-vote-btn up ${v === 1 ? 'on' : ''}`} onClick={() => vote(p.id, 1)} aria-label="Votar a favor"><VoteArrow /></button>
                <span className={`cf-vote-score ${v === 1 ? 'up' : v === -1 ? 'down' : ''}`}>{scoreOf(p.id, p.score)}</span>
                <button type="button" className={`cf-vote-btn down ${v === -1 ? 'on' : ''}`} onClick={() => vote(p.id, -1)} aria-label="Votar en contra"><VoteArrow down /></button>
              </div>

              <div className="cf-post-main">
                <div className="cf-post-head">
                  <button type="button" className="cf-author" onClick={() => onNavigate && onNavigate(p.author.name)} title={`Ver perfil de @${p.author.name}`}>
                    <Avatar u={{ username: p.author.name, name: p.author.name, avatar: p.author.avatar }} frame={tier.frame} size={38} />
                    <span className="cf-author-id">
                      <span className="cf-author-name">@{p.author.name}<span className="cf-tier" style={{ color: tier.color, borderColor: `${tier.color}55` }}><PixelMedal color={tier.color} fit={14} className="cf-tier-medal" /> {tier.label}</span></span>
                      <span className="cf-author-meta">
                        {p.author.trades > 0 && <><span className="fin-num">{p.author.trades}</span> trades</>}
                        {p.author.rating && <> · <FaStar className="cf-star" /> <span className="fin-num">{p.author.rating}</span></>}
                        <> · {timeLabel(p.ts)}</>
                      </span>
                    </span>
                  </button>
                  <span className="cf-sentiment" style={{ color: s.color }}><SIcon /> {s.label}</span>
                </div>

                {editingId === p.id ? (
                  <div className="cf-edit-box">
                    <textarea className="cf-input" rows={3} value={editDraft} onChange={(e) => setEditDraft(e.target.value.slice(0, MAX_LEN))} autoFocus />
                    <div className="cf-edit-actions">
                      <span className={`cf-count ${editDraft.length > MAX_LEN - 30 ? 'warn' : ''}`}>{MAX_LEN - editDraft.length}</span>
                      <button type="button" className="cf-link-btn" onClick={() => { setEditingId(null); setEditDraft(''); }}>Cancelar</button>
                      <button type="button" className="cf-cmt-send" disabled={!editDraft.trim()} onClick={() => saveEdit(p.id)}><FiSend /> Guardar</button>
                    </div>
                  </div>
                ) : (
                  p.text && <p className="cf-post-text">{renderText(p.text, (sym) => onViewAsset && onViewAsset(sym))}{p.edited && <span className="cf-edited"> · editado</span>}</p>
                )}
                {p.image && <img className="cf-post-img" src={p.image} alt="" loading="lazy" />}

                {(p.assets || []).length > 0 && (
                  <div className="cf-post-assets">
                    {p.assets.map((a) => { const n = offerCount(a); return (
                      <button key={a} type="button" className="cf-offer-link" onClick={() => onViewAsset && onViewAsset(a)}>
                        <CoinIcon sym={a} size={16} /> Ver ofertas de <strong>${a}</strong>{n > 0 && <span className="cf-offer-n">{n}</span>} <FiArrowRight />
                      </button>
                    ); })}
                  </div>
                )}

                {/* reacciones pixel */}
                <div className="cf-reacts">
                  {REACTIONS.map(({ key, label }) => { const on = !!reacts[p.id]?.[key]; const n = reactCount(p.id, key, p.reacts?.[key]); return (
                    <button key={key} type="button" className={`cf-react ${on ? 'on' : ''}`} title={label} onClick={() => toggleReact(p.id, key)}>
                      <PixelArt sprite={key} px={2} className="cf-react-art" />
                      {n > 0 && <span className="fin-num">{n}</span>}
                    </button>
                  ); })}
                  <button type="button" className={`cf-cmt-toggle ${open ? 'on' : ''}`} onClick={() => toggleThread(p.id)}>
                    <FiCornerDownRight /> {nComments > 0 ? <><span className="fin-num">{nComments}</span> {nComments === 1 ? 'comentario' : 'comentarios'}</> : 'Comentar'}
                  </button>
                  <button type="button" className={`cf-save ${saved.includes(p.id) ? 'on' : ''}`} onClick={() => toggleSave(p.id)} title={saved.includes(p.id) ? 'Quitar de guardados' : 'Guardar'}><FiBookmark /></button>
                  {isMine(p) ? (
                    <>
                      <button type="button" className="cf-mine-btn" onClick={() => startEdit(p)} title="Editar tu publicación"><FiEdit2 /></button>
                      <button type="button" className="cf-mine-btn del" onClick={() => deletePost(p.id)} title="Eliminar tu publicación"><FiTrash2 /></button>
                    </>
                  ) : (
                    <button type="button" className="cf-report" onClick={() => hidePost(p)} title="Reportar / ocultar">Reportar</button>
                  )}
                </div>

                {open && renderThread(p)}
              </div>
            </article>
          );
        })}
      </div>

      {toast && <div className="cf-toast">{toast}</div>}
    </div>
  );
}
